import { API_ENDPOINTS, apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

interface UseNetworkRecoveryProps {
  isGenerating: boolean;
  onPicksRecovered: (picks: unknown, generationId: string) => void;
}

interface UndeliveredResponse {
  undelivered: {
    generationId: string;
    picks: unknown;
    createdAt: string;
    analysisMode: string;
  } | null;
}

/**
 * Automatically recovers undelivered picks when network resumes after a drop.
 * Only checks if network dropped during active generation.
 */
export function useNetworkRecovery({ isGenerating, onPicksRecovered }: UseNetworkRecoveryProps) {
  const { authFetch } = useAuth();
  const wasGeneratingRef = useRef(false);

  useEffect(() => {
    // Track if we were generating when network dropped
    const handleOffline = () => {
      if (isGenerating) {
        wasGeneratingRef.current = true;
      }
    };

    // Auto-recover when network resumes
    const handleOnline = async () => {
      if (wasGeneratingRef.current) {
        wasGeneratingRef.current = false;

        try {
          const res = await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_UNDELIVERED));

          if (!res.ok) return;

          const data = (await res.json()) as UndeliveredResponse;

          if (data.undelivered?.picks) {
            // Recover picks
            onPicksRecovered(data.undelivered.picks, data.undelivered.generationId);

            // Show notification
            toast.success('Recovered your picks from earlier!', {
              duration: 4000,
              icon: '🔄',
            });

            // Mark as delivered
            await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_CONFIRM), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ generationId: data.undelivered.generationId }),
            });
          }
        } catch {
          // Silent fail - user can manually regenerate
        }
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [isGenerating, onPicksRecovered, authFetch]);
}
